/**
 * Unified route utilities that handle both database and static course routing
 * Provides consistent navigation patterns across the application
 */

import { generateCourseRoute, generateModuleRoute, determineCourseType, CourseType } from './routeUtils';
import { getCourseTypeFromCategory } from './categoryRouteMapping';

export interface UnifiedRouteConfig {
  courseId: string;
  categoryId?: string;
  moduleId?: string;
  courseType?: CourseType;
}

/**
 * Generate unified course route that works with both database and static courses
 */
export function generateUnifiedCourseRoute(config: UnifiedRouteConfig): string {
  const { courseId, categoryId, courseType } = config;
  
  // Try to determine course type from category if not provided
  let finalCourseType = courseType;
  if (!finalCourseType && categoryId) {
    finalCourseType = mapCategoryToCourseType(categoryId);
  }
  
  // Use existing route generation logic
  return generateCourseRoute(courseId, finalCourseType);
}

/**
 * Generate unified module route
 */
export function generateUnifiedModuleRoute(config: UnifiedRouteConfig): string {
  const { courseId, moduleId, categoryId, courseType } = config;
  
  if (!moduleId) {
    throw new Error('Module ID is required for module routes');
  }
  
  // Try to determine course type from category if not provided
  let finalCourseType = courseType;
  if (!finalCourseType && categoryId) {
    finalCourseType = mapCategoryToCourseType(categoryId);
  }
  
  return generateModuleRoute(courseId, moduleId, finalCourseType);
}

/**
 * Map database category IDs to course types for routing
 * Uses centralized mapping from categoryRouteMapping
 */
export function mapCategoryToCourseType(categoryId: string): CourseType {
  return getCourseTypeFromCategory(categoryId);
}

/**
 * Get category listing route
 */
export function getCategoryListingRoute(categoryId: string): string {
  const courseType = mapCategoryToCourseType(categoryId);
  
  switch (courseType) {
    case 'sql': return '/sql-courses';
    case 'python': return '/python-courses';
    case 'ai': return '/ai-courses';
    case 'java': return '/java-courses';
    case 'aws': return '/aws-courses';
    case 'azure': return '/azure-courses';
    case 'gcp': return '/gcp-courses';
    case 'dotnet': return '/dotnet-courses';
    case 'dataengineering': return '/dataengineering-courses';
    case 'javascript': return '/javascript-courses';
    case 'ml': return '/ml-courses';
    case 'datascience': return '/datascience-courses';
    case 'powerbi': return '/powerbi-courses';
    case 'tableau': return '/tableau-courses';
    case 'devops': return '/devops-courses';
    case 'appsec': return '/appsec-courses';
    case 'infrastructure': return '/infrastructure-courses';
    case 'frontend-advanced': return '/frontend-advanced-courses';
    case 'testing-modern': return '/testing-modern-courses';
    case 'web3-development': return '/web3-development-courses';
    case 'serverless-edge': return '/serverless-edge-courses';
    case 'rpa-tools': return '/rpa-tools-courses';
    case 'etl': return '/etl-courses';
    case 'react': return '/react-courses';
    case 'workflow-automation': return '/workflow-automation-courses';
    default: return `/courses?category=${categoryId}`;
  }
}

/**
 * Parse route to extract course and module information
 */
export function parseUnifiedRoute(pathname: string): {
  courseType?: CourseType;
  courseId?: string;
  moduleId?: string;
  categoryId?: string;
} {
  // Handle category-specific routes
  const categoryRouteMatch = pathname.match(/^\/(\w+)-courses(?:\/([^\/]+))?(?:\/([^\/]+))?/);
  if (categoryRouteMatch) {
    const [, category, courseId, moduleId] = categoryRouteMatch;
    const courseType = category as CourseType;
    
    return {
      courseType,
      courseId,
      moduleId,
      categoryId: category
    };
  }
  
  // Handle generic course routes
  const genericRouteMatch = pathname.match(/^\/courses(?:\/([^\/]+))?(?:\/([^\/]+))?/);
  if (genericRouteMatch) {
    const [, courseId, moduleId] = genericRouteMatch;
    
    return {
      courseId,
      moduleId,
      courseType: courseId ? determineCourseType(courseId) : undefined
    };
  }
  
  return {};
}

/**
 * Check if a route is a course-related route
 */
export function isCourseRoute(pathname: string): boolean {
  return /^\/(?:\w+-)?courses/.test(pathname) || /^\/courses/.test(pathname);
}